import { Link, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useDataProvider, useGetOne, useNotify, useTranslate } from "ra-core";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext";
import { TaskChecklist } from "./TaskChecklist";
import { TaskProcedureButton } from "./TaskProcedureButton";
import {
  dateInputToIso,
  fullName,
  isoToDateInput,
  nativeSelectClass,
  useTaskAreas,
  useTeam,
} from "./taskData";

interface TaskRecord {
  id: number;
  text: string;
  description: string | null;
  status: string;
  due_date: string | null;
  sales_id: number | null;
  area_id: number | null;
  priority: number;
  type: string | null;
  contact_id: number | null;
  template_id: number | null;
}

// One task: fields saved one at a time through tenax_update_task, the
// checklist, and the closing actions. "Cancel" replaces Atomic's delete
// (D-018, point 3).
export const TenaxTaskShow = () => {
  const { id } = useParams();
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const { taskTypes } = useConfigurationContext();
  const { data: areas = [] } = useTaskAreas();
  const { data: team = [] } = useTeam();
  const { data: task } = useGetOne<TaskRecord>("tasks", { id: Number(id) });

  if (!task) return null;

  const save = async (data: Partial<TaskRecord>) => {
    try {
      await dataProvider.update("tasks", {
        id: task.id,
        data,
        previousData: task,
      });
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
    await queryClient.invalidateQueries({ queryKey: ["tasks"] });
  };

  const isClosed = task.status === "done" || task.status === "cancelled";

  const body = (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={isClosed ? "secondary" : "default"}>
          {translate(`tenax.tasks.status.${task.status}`)}
        </Badge>
        {task.contact_id && (
          <Link
            className="text-sm underline"
            to={`/contacts/${task.contact_id}/show`}
          >
            {translate("resources.contacts.forcedCaseName")}
          </Link>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="task-title">
          {translate("tenax.tasks.fields.title")}
        </Label>
        <Input
          id="task-title"
          key={`title-${task.text}`}
          defaultValue={task.text}
          onBlur={(event) => {
            const value = event.target.value.trim();
            if (value && value !== task.text) save({ text: value });
          }}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="task-area">
            {translate("tenax.tasks.fields.area")}
          </Label>
          <select
            id="task-area"
            className={nativeSelectClass}
            value={task.area_id ?? ""}
            onChange={(event) =>
              save({
                area_id: event.target.value ? Number(event.target.value) : null,
              })
            }
          >
            <option value="">{translate("tenax.tasks.fields.no_area")}</option>
            {areas
              .filter((area) => area.active || area.id === task.area_id)
              .map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="task-assignee">
            {translate("tenax.tasks.fields.assignee")}
          </Label>
          <select
            id="task-assignee"
            className={nativeSelectClass}
            value={task.sales_id ?? ""}
            onChange={(event) => save({ sales_id: Number(event.target.value) })}
          >
            {team.map((member) => (
              <option key={member.id} value={member.id}>
                {fullName(member)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="task-due">
            {translate("tenax.tasks.fields.due_date")}
          </Label>
          <Input
            id="task-due"
            type="date"
            value={isoToDateInput(task.due_date)}
            onChange={(event) =>
              save({ due_date: dateInputToIso(event.target.value) })
            }
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="task-priority">
            {translate("tenax.tasks.fields.priority")}
          </Label>
          <select
            id="task-priority"
            className={nativeSelectClass}
            value={task.priority}
            onChange={(event) => save({ priority: Number(event.target.value) })}
          >
            {[1, 2, 3].map((level) => (
              <option key={level} value={level}>
                {translate(`tenax.tasks.priority.${level}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="task-type">
            {translate("tenax.tasks.fields.type")}
          </Label>
          <select
            id="task-type"
            className={nativeSelectClass}
            value={task.type ?? ""}
            onChange={(event) => save({ type: event.target.value })}
          >
            {taskTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="task-description">
          {translate("tenax.tasks.fields.description")}
        </Label>
        <Textarea
          id="task-description"
          key={`description-${task.description ?? ""}`}
          rows={3}
          defaultValue={task.description ?? ""}
          onBlur={(event) => {
            if (event.target.value !== (task.description ?? "")) {
              save({ description: event.target.value });
            }
          }}
        />
      </div>

      {task.template_id && (
        <TaskProcedureButton templateId={task.template_id} />
      )}

      <TaskChecklist taskId={task.id} />

      <div className="flex flex-wrap gap-2">
        {isClosed ? (
          <Button variant="outline" onClick={() => save({ status: "todo" })}>
            {translate("tenax.tasks.actions.reopen")}
          </Button>
        ) : (
          <>
            <Button onClick={() => save({ status: "done" })}>
              {translate("tenax.tasks.actions.done")}
            </Button>
            <Button
              variant="outline"
              onClick={() => save({ status: "cancelled" })}
            >
              {translate("tenax.tasks.actions.cancel")}
            </Button>
          </>
        )}
      </div>
    </div>
  );

  const back = (
    <Button
      asChild
      variant="ghost"
      size="icon"
      aria-label={translate("tenax.tasks.actions.back")}
    >
      <Link to="/tasks">
        <ArrowLeft />
      </Link>
    </Button>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <div className="flex min-w-0 items-center gap-1">
            {back}
            <h1 className="truncate text-lg font-semibold">{task.text}</h1>
          </div>
        </MobileHeader>
        <MobileContent>{body}</MobileContent>
      </>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 py-6">
      <div className="flex items-center gap-2">
        {back}
        <h1 className="text-2xl font-semibold">{task.text}</h1>
      </div>
      {body}
    </div>
  );
};
